// src/pages/PlatformGuide.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";

type Topic = {
  id: string;       // anchor id
  title: string;    // topic title
  items: string[];  // bullets
};

const PlatformGuide: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // lock page scroll (only inner panel scrolls)
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const topics: Topic[] = useMemo(() => {
    const bullets = (key: string): string[] => {
      const v = t(key, { returnObjects: true });
      return Array.isArray(v) ? (v as string[]) : [];
    };

    return [
      {
        id: "gallery",
        title: t("help.platform.topics.gallery.title"),
        items: bullets("help.platform.topics.gallery.bullets"),
      },
      {
        id: "preferences",
        title: t("help.platform.topics.preferences.title"),
        items: bullets("help.platform.topics.preferences.bullets"),
      },
      {
        id: "mylikes",
        title: t("help.platform.topics.mylikes.title"),
        items: bullets("help.platform.topics.mylikes.bullets"),
      },
      {
        id: "changeLocation",
        title: t("help.platform.topics.changeLocation.title"),
        items: bullets("help.platform.topics.changeLocation.bullets"),
      },
      {
        id: "uploadPost",
        title: t("help.platform.topics.uploadPost.title"),
        items: bullets("help.platform.topics.uploadPost.bullets"),
      },
      {
        id: "musicInMedia",
        title: t("help.platform.topics.musicInMedia.title"),
        items: bullets("help.platform.topics.musicInMedia.bullets"),
      },
      {
        id: "myProfile",
        title: t("help.platform.topics.myProfile.title"),
        items: bullets("help.platform.topics.myProfile.bullets"),
      },
      {
        id: "myPosts",
        title: t("help.platform.topics.myPosts.title"),
        items: bullets("help.platform.topics.myPosts.bullets"),
      },
      {
        id: "followersFollowing",
        title: t("help.platform.topics.followersFollowing.title"),
        items: bullets("help.platform.topics.followersFollowing.bullets"),
      },
      {
        id: "visitProfiles",
        title: t("help.platform.topics.visitProfiles.title"),
        items: bullets("help.platform.topics.visitProfiles.bullets"),
      },
      {
        id: "notifications",
        title: t("help.platform.topics.notifications.title"),
        items: bullets("help.platform.topics.notifications.bullets"),
      },
      {
        id: "chat",
        title: t("help.platform.topics.chat.title"),
        items: bullets("help.platform.topics.chat.bullets"),
      },
      {
        id: "safety",
        title: t("help.platform.topics.safety.title"),
        items: bullets("help.platform.topics.safety.bullets"),
      },
    ];
  }, [t]);

  const [activeAnchor, setActiveAnchor] = useState<string>("pg-top");

  const scrollTo = (id: string) => {
  const container = document.getElementById("pg-scroll");
  const el = document.getElementById(id);
  if (!container || !el) return;

  const top =
    el.getBoundingClientRect().top -
    container.getBoundingClientRect().top +
    container.scrollTop -
    12;

  container.scrollTo({ top, behavior: "smooth" });

  // Update URL WITHOUT triggering browser scroll
  window.history.replaceState(null, "", `#${id}`);
};

  // Deep link on load: /help/platform#topic-id
  useEffect(() => {
  const hash = (location.hash || "").replace("#", "").trim();
  if (!hash) return;

  requestAnimationFrame(() => {
    scrollTo(hash);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // Active anchor tracking
  useEffect(() => {
    const container = document.getElementById("pg-scroll");
    if (!container) return;

    const handler = () => {
  let current = "pg-top";
  const ids = ["pg-top", ...topics.map((t) => t.id)];

  for (const id of ids) {
    const el = document.getElementById(id);
    if (!el) continue;

    const elTop =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top;

    if (elTop <= 120) current = id;
  }

  if (current !== activeAnchor) {
    setActiveAnchor(current);
  }
};


    handler();
    container.addEventListener("scroll", handler, { passive: true });
    return () => container.removeEventListener("scroll", handler as any);
  }, [topics]);

  return (
    <div style={shell}>
      {/* Top bar */}
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
  <HighLogo
    src={logo}
    style={{ width: 100, display: "block", marginLeft: "-20px", marginTop: "6px" }}
    padding={0}
    onClick={() => navigate("/gallery")}
  />
</div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", marginRight: 70 }}>
          <div style={jumpTitle}>{t("help.platform.pageTitle")}</div>
        </div>
      </header>

      <main style={main}>
         <div style={jumpHint}>{t("help.platform.ui.jumpHint")}</div>

        {/* Top jump bar */}
        <section style={jumpBar}>

          <div style={jumpPillsWrap}>
            <button
              type="button"
              onClick={() => scrollTo("pg-top")}
              className="pg-link"
              style={{ ...pillBtn, ...(activeAnchor === "pg-top" ? pillActive : null) }}
            >
              {t("help.platform.ui.overview")}
            </button>

            {topics.map((tp) => (
              <button
                key={tp.id}
                type="button"
                onClick={() => scrollTo(tp.id)}
                className="pg-link"
                style={{ ...pillBtn, ...(activeAnchor === tp.id ? pillActive : null) }}
                title={tp.title}
              >
                {tp.title.split(":")[0]}
              </button>
            ))}
          </div>
        </section>

        {/* Content card */}
        <section style={contentCard}>
          <div id="pg-scroll" style={contentScroll}>
            <div id="pg-top" />

            <div style={pageIntro}>
              <h1 style={h1}>{t("help.platform.pageTitle")}</h1>
              <p style={subhead}>{t("help.platform.pageSubtitle")}</p>
            </div>

            <div style={topicColumn}>
              {topics.map((tp) => (
                <article key={tp.id} id={tp.id} style={topicCard}>
                  <div style={topicTitleRow}>
                    <button
                      type="button"
                      className="pg-link"
                      onClick={() => scrollTo(tp.id)}
                      style={topicTitleBtn}
                      aria-label={t("help.platform.ui.linkToTopicAria", { title: tp.title })}
                      title={t("help.platform.ui.linkToTopicTitle", { title: tp.title })}
                    >
                      {tp.title}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const url = `${window.location.origin}${window.location.pathname}#${tp.id}`;
                        navigator.clipboard?.writeText(url).catch(() => {});
                      }}
                      style={copyBtn}
                      title={t("help.platform.ui.copyLink")}
                      aria-label={t("help.platform.ui.copyLink")}
                    >
                      ⧉
                    </button>
                  </div>

                  <ul style={topicList}>
                    {tp.items.map((it, idx) => (
                      <li key={idx} style={topicItem}>
                        <span style={dot}>•</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>

                  <div style={topicFooter}>
                    <button
                      type="button"
                      className="pg-link"
                      onClick={() => scrollTo("pg-top")}
                      style={backTopBtn}
                      title={t("help.platform.ui.backToTop")}
                    >
                      ↑ {t("help.platform.ui.backToTop")}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <style>{`
        .pg-link { transition: color 140ms ease, opacity 140ms ease, border-color 140ms ease; }
        .pg-link:hover { color: #e5e7eb !important; opacity: 1 !important; cursor: pointer; }
        .pg-link:focus-visible { outline: 2px solid rgba(163,230,53,0.6); outline-offset: 3px; border-radius: 12px; }
      `}</style>
    </div>
  );
};

export default PlatformGuide;

/* ----------------- styles (same style system as your other help pages) ----------------- */

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 40%, #020617 100%)",
  color: "#b0b8c0",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 25px",
  background: "rgba(2, 6, 23, 0.9)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.2)",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
};

const main: React.CSSProperties = {
  flex: 1,
  display: "grid",
  gridTemplateRows: "auto minmax(0, 1fr)",
  gap: 14,
  padding: "18px 3vw 18px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const jumpBar: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.85), rgba(3,7,18,1))",
  boxShadow: "0 18px 45px rgba(0,0,0,0.75)",
  padding: "25px 14px",
  marginTop: -6,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
  overflow: "hidden",
};

const jumpTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 900,
  letterSpacing: -0.02,
  marginTop: -5,
  color: "#b0b8c0",
};

const jumpHint: React.CSSProperties = {
  marginTop: 0,
  marginBottom: 0,
  fontSize: 12,
  color: "#63696f",
};

const jumpPillsWrap: React.CSSProperties = {
  flex: "1 1 auto",
  display: "flex",
  gap: 10,
  overflowX: "auto",
  paddingBottom: 2,
};

const pillBtn: React.CSSProperties = {
  flex: "0 0 auto",
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(255,255,255,0.04)",
  padding: "8px 12px",
  color: "#b0b8c0",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const pillActive: React.CSSProperties = {
  border: "1px solid rgba(163,230,53,0.35)",
  background: "rgba(163,230,53,0.10)",
  color: "#b0b8c0",
};

const contentCard: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.90), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
  marginTop: "45px",
};

const contentScroll: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "18px 18px 45px",
  boxSizing: "border-box",
};

const pageIntro: React.CSSProperties = {
  padding: "6px 2px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.14)",
};

const h1: React.CSSProperties = {
  margin: 0,
  fontSize: 22,
  letterSpacing: -0.03,
  lineHeight: 1.1,
};

const subhead: React.CSSProperties = {
  margin: "10px 0 0",
  color: "#8d9298",
  fontSize: 15,
  lineHeight: 1.55,
  maxWidth: 980,
};

const topicColumn: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  marginTop: 14,
};

const topicCard: React.CSSProperties = {
  borderRadius: 18,
  border: "1px solid rgba(148,163,184,0.18)",
  background: "rgba(255,255,255,0.04)",
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
};

const topicTitleRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
};

const topicTitleBtn: React.CSSProperties = {
  flex: "1 1 auto",
  textAlign: "left",
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  color: "#b0b8c0",
  fontSize: 18,
  fontWeight: 950,
  letterSpacing: -0.02,
  cursor: "pointer",
};

const copyBtn: React.CSSProperties = {
  flex: "0 0 auto",
  width: 34,
  height: 34,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  color: "#b1b8c7",
  cursor: "pointer",
};

const topicList: React.CSSProperties = {
  margin: "12px 0 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 9,
};

const topicItem: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  color: "#b0b8c0",
  fontSize: 14,
  lineHeight: 1.55,
};

const dot: React.CSSProperties = {
  color: "rgba(148,163,184,0.85)",
  flex: "0 0 auto",
  marginTop: 3,
};

const topicFooter: React.CSSProperties = {
  marginTop: 12,
  display: "flex",
  justifyContent: "flex-end",
};

const backTopBtn: React.CSSProperties = {
  borderRadius: 999,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  padding: "8px 12px",
  color: "#647282",
  fontSize: 12.5,
  fontWeight: 800,
  cursor: "pointer",
};
